import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import { AdminCourseNotFound } from "@/features/admin/components/AdminCourseNotFound";
import { EditLiveModulesPage } from "@/features/admin/edit-course/EditLiveModulesPage";
import { getLiveVersion } from "@/features/admin/edit-course/courseAvailability";
import { adminGetCourse } from "@/services/admin/courses";
import type { CourseDetailDto } from "@/features/admin/types";

export const Route = createFileRoute("/admin/courses/$courseId_/versions/$versionId/modules")({
  component: EditLiveModulesRoute,
  remountDeps: ({ params }) => `${params.courseId}/${params.versionId}`,
});

/** Edit modules on the live version. Any other version goes back to the Content and versions tab. */
function EditLiveModulesRoute() {
  const { courseId, versionId } = Route.useParams();

  // The same query as the edit course page, so going back and forth shares one copy of the course.
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "courses", courseId],
    queryFn: ({ signal }) => adminGetCourse(courseId, signal),
    retry: (failureCount, error) => {
      if (isAxiosError(error) && error.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  }) as { data: CourseDetailDto | undefined; isLoading: boolean; error: Error | null };

  // A failed refetch keeps the page, and any unsaved edits, on the copy of the course it has.
  if (error && !data) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return (
        <ProtectedRoute requiredPermissions={["Courses.Read"]} requiredRoles={["Admin"]}>
          <AdminCourseNotFound />
        </ProtectedRoute>
      );
    }
    return (
      <ProtectedRoute requiredPermissions={["Courses.Read"]} requiredRoles={["Admin"]}>
        <AdminPageLayout mainClassName="bg-secondary_alt">
          <div className="bg-secondary_alt flex min-h-[60vh] items-center justify-center">
            <p className="text-sm text-red-600">Unable to load course details.</p>
          </div>
        </AdminPageLayout>
      </ProtectedRoute>
    );
  }

  if (isLoading || !data) {
    return (
      <ProtectedRoute requiredPermissions={["Courses.Read"]} requiredRoles={["Admin"]}>
        <AdminPageLayout mainClassName="bg-secondary_alt">
          <div className="bg-secondary_alt flex min-h-[60vh] items-center justify-center">
            <p className="text-tertiary text-sm">Loading course details…</p>
          </div>
        </AdminPageLayout>
      </ProtectedRoute>
    );
  }

  const liveVersion = getLiveVersion(data);
  if (!liveVersion?.id || liveVersion.id !== versionId) {
    return (
      <Navigate
        to="/admin/courses/$courseId"
        params={{ courseId }}
        search={{ tab: "content" }}
        replace
      />
    );
  }

  return (
    <ProtectedRoute requiredPermissions={["Courses.Read"]} requiredRoles={["Admin"]}>
      <AdminPageLayout mainClassName="bg-secondary_alt">
        <EditLiveModulesPage
          courseId={courseId}
          course={data}
          version={{ ...liveVersion, id: liveVersion.id }}
        />
      </AdminPageLayout>
    </ProtectedRoute>
  );
}
